import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Notice, Pager, Stat } from "../../lib/ui";

/*
 * Chart of accounts with balances – port of accountBalance.php (API: /api/accounts/balances)
 * Balance by account type: Asset / Expense / Contra Liability = debits − credits; Liability / Equity / Revenue /
 * Contra Asset = credits − debits. Table or card view, filters, sorting, 15 per page, CSV export.
 */

const TONE = { Asset: ["#d1fae5", "#065f46"], Liability: ["#fee2e2", "#7f1d1d"], Equity: ["#ede9fe", "#4c1d95"], Revenue: ["#dbeafe", "#1e3a8a"],
  Expense: ["#fef3c7", "#78350f"], "Contra Asset": ["#fce7f3", "#831843"], "Contra Liability": ["#e0f2fe", "#0c4a6e"] };
const Type = ({ t }) => { const [bg, fg] = TONE[t] || ["#f3f4f6", "#374151"]; return <span className="pill-tag" style={{ background: bg, color: fg }}>{t}</span>; };

export default function AccountBalances() {
  const api = useApi();
  const [f, setF] = useState({ search: "", type: "", sort: "account_number", order: "ASC", page: 1 });
  const [term, setTerm] = useState("");
  const [view, setView] = useState("table");
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async (q) => { try { setD(await api.get("/api/accounts/balances", q)); } catch (e) { setError(e.message); } }, [api]);
  useEffect(() => { load(f); }, [load, f]);
  useEffect(() => { const t = setTimeout(() => setF((s) => (s.search === term ? s : { ...s, search: term, page: 1 })), 300); return () => clearTimeout(t); }, [term]);
  const sortBy = (k) => setF((s) => ({ ...s, sort: k, order: s.sort === k && s.order === "ASC" ? "DESC" : "ASC", page: 1 }));
  const csv = () => { const u = new URL(api.base + "/api/accounts/balances"); ["search", "type", "sort", "order"].forEach((k) => u.searchParams.set(k, f[k])); u.searchParams.set("export", "csv"); return u.toString(); };
  const bt = d?.by_type || {};
  const Th = ({ k, children, num }) => <th className={`sortable ${num ? "num" : ""}`} onClick={() => sortBy(k)}>{children} {f.sort === k && <i className={`fas ${f.order === "ASC" ? "fa-sort-up" : "fa-sort-down"}`} />}</th>;

  return (
    <main className="pg">
      <PageHeader slug="accountBalance" actions={<><Link to="/account_form" className="btn"><i className="fas fa-plus" /> Add account</Link>
        <a className="btn" href={csv()}><i className="fas fa-file-csv" /> Export CSV</a></>} />
      <Notice notice={error && { tone: "err", text: error }} />
      {d && (
        <div className="ui-stats">
          <Stat icon="fa-book" tone="muted" label="Active accounts" value={d.total_accounts} />
          <Stat icon="fa-building-columns" tone="up" label="Assets" value={n2(bt.Asset?.balance)} unit="TZS" />
          <Stat icon="fa-scale-balanced" tone="danger" label="Liabilities" value={n2(bt.Liability?.balance)} unit="TZS" />
          <Stat icon="fa-landmark" tone="brand" label="Equity" value={n2(bt.Equity?.balance)} unit="TZS" />
          <Stat icon="fa-arrow-trend-up" tone="warn" label="Revenue" value={n2(bt.Revenue?.balance)} unit="TZS" />
        </div>
      )}
      <section className="panel">
        <div className="toolbar">
          <div className="field grow"><div className="search"><i className="fas fa-magnifying-glass" />
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Account name or number" aria-label="Search accounts" /></div></div>
          <div className="field"><select aria-label="Account type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value, page: 1 })}>
            <option value="">All types</option>{(d?.types || []).map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="seg-filter" role="group" aria-label="View">{[["table", "Table"], ["cards", "Cards"]].map(([k, l]) =>
            <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)}>{l}</button>)}</div>
        </div>
        {d && view === "table" && (
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr><Th k="account_number">Number</Th><Th k="account_name">Account</Th><Th k="account_type">Type</Th><Th k="balance" num>Balance</Th><th className="num">Entries</th><th /></tr></thead>
            <tbody>{d.rows.map((r) => (
              <tr key={r.account_number}><td className="mono">{r.account_number}</td><td style={{ fontWeight: 600, color: "var(--ink)" }}>{r.account_name}</td>
                <td><Type t={r.account_type} /></td><td className="num mono" style={{ color: r.balance < 0 ? "var(--danger)" : undefined }}>{n2(r.balance)}</td>
                <td className="num mono">{r.transaction_count}</td>
                <td style={{ whiteSpace: "nowrap" }}><Link className="btn sm" to={`/accountStatement?account_id=${r.account_number}&from_date=${new Date().getFullYear()}-01-01&to_date=${new Date().toISOString().slice(0, 10)}`}>Statement</Link>{" "}
                  {r.id && <Link className="btn sm" to={`/account_form/${r.id}`}>Edit</Link>}</td></tr>))}</tbody>
          </table></div>
        )}
        {d && view === "cards" && (
          <div className="ab-cards">{d.rows.map((r) => (
            <div key={r.account_number} className="ab-card"><div style={{ display: "flex", justifyContent: "space-between" }}><span className="mono">{r.account_number}</span><Type t={r.account_type} /></div>
              <b>{r.account_name}</b><span className="mono" style={{ fontSize: "1.15rem" }}>TZS {n2(r.balance)}</span><small>{r.transaction_count} entries</small></div>))}</div>
        )}
        {d && d.rows.length === 0 && <div className="empty"><i className="fas fa-book" />No accounts match.</div>}
        {d && <Pager page={d.page} totalPages={d.pages} onPage={(p) => setF({ ...f, page: p })} />}
      </section>
    </main>
  );
}
